// getSelectedFeedQueryOptions.js
'use strict';
import flatten from 'lodash/flatten';

import { getCandidateQueryOptionObject } from '../selector/getFeedQueryObject.js';
import getFilterType from '../selector/getFilterType.js';
import getRouterData from '../selector/getRouterData.js';
import getFilterGroupMap from '../selector/getFilterGroupMap.js';

import createCachedSelector from '../resource/createCachedSelector.js';
import {
  parseQueryOptions,
  parseOptionWithFilterGroupMap,
} from '../resource/feedUtils.js';
import { OPTION_UNSET, QUERY_SEPARATOR } from '../resource/feedConstants.js';

const defaultObject = {};
const defaultArray = [];

/**
 * Get selected feed query options by remote config key name and query type.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} remoteConfigKeyName - remote config key.
 * @param {QueryType} queryType - query type.
 * @param {object} [location] - explicit location override, `{ pathname, search }`.
 * @return {array} Return selected query options.
 */
const getSelectedFeedQueryOptions = createCachedSelector(
  (state, remoteConfigKeyName, queryType, location) =>
    getCandidateQueryOptionObject(
      state,
      remoteConfigKeyName,
      queryType,
      location
    ),
  (state, remoteConfigKeyName, queryType, location) =>
    getFeedQueryOptionsFromUrl(state, location),
  (state, remoteConfigKeyName, queryType, location) =>
    getFilterType(state, location?.pathname),
  (state, remoteConfigKeyName, queryType, location) =>
    getFilterGroupMap(state, getFilterType(state, location?.pathname)),
  (
    optionGroupObject = defaultObject,
    queries = defaultArray,
    filterType = 'v1',
    filterGroupMap = defaultObject
  ) => {
    const {
      intersectedFeedNames: selectedIntersectedFeedNames,
      filters: selectedFilters,
      sorts: selectedSorts,
    } = parseQueryOptions({ options: queries });
    let selectedOptions = [];

    switch (filterType) {
      case 'v1': {
        selectedOptions = flatten(Object.values(optionGroupObject)).filter(
          option => {
            if (!option || OPTION_UNSET === option) {
              return false;
            }

            const {
              intersectedFeedNames: optionIntersectedFeedNames,
              filters: optionFilters,
              sorts: optionSorts,
            } = parseQueryOptions({ options: [option] });

            const isFeedMatched =
              (!selectedIntersectedFeedNames.length &&
                !optionIntersectedFeedNames.length) ||
              optionIntersectedFeedNames.every(name =>
                selectedIntersectedFeedNames.includes(name)
              );

            const isFiltersMatched = optionFilters.every(filter =>
              selectedFilters.includes(filter)
            );
            const isSortsMatched = optionSorts.every(sort =>
              selectedSorts.includes(sort)
            );

            return isFeedMatched && isFiltersMatched && isSortsMatched;
          }
        );
        break;
      }
      case 'v2':
        // ['user_livestream-global?filters_v2=category:toy']
        selectedOptions = flatten(Object.values(optionGroupObject)).filter(
          option => {
            if (!option || OPTION_UNSET === option) {
              return false;
            }

            const {
              intersectedFeedNames: optionIntersectedFeedNames,
              filters: optionFilters,
              sorts: optionSorts,
            } = parseQueryOptions({ options: [option] });

            const isFeedMatched =
              (!selectedIntersectedFeedNames.length &&
                !optionIntersectedFeedNames.length) ||
              optionIntersectedFeedNames.every(name =>
                selectedIntersectedFeedNames.includes(name)
              );

            const isFiltersMatched = optionFilters.every(filter =>
              selectedFilters.some(selectedFilter =>
                selectedFilter.includes(filter)
              )
            );
            const isSortsMatched = optionSorts.every(sort =>
              selectedSorts.includes(sort)
            );

            return isFeedMatched && isFiltersMatched && isSortsMatched;
          }
        );

        // ['LIVESTREAM_FEED_FILTERS_CATEGORY/user_livestream-global?filters_v2=category:toy']
        selectedOptions = parseOptionWithFilterGroupMap({
          options: selectedOptions,
          filterGroupMap,
        });

        break;
    }

    return selectedOptions;
  }
)(
  (state, remoteConfigKeyName, queryType, location) =>
    `${remoteConfigKeyName}:${queryType}:${location?.pathname ?? ''}${
      location?.search ?? ''
    }`
);

export default getSelectedFeedQueryOptions;

/**
 * Select queries from url
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {object} [location] - explicit location override, `{ pathname, search }`.
 * @return {array} The selected queries from url.
[
  'user_livestream-global-filtered&user_creators',
  '?filters=region:asia',
  '?filters=region:naeu',
  '?filters=preset:preview',
  '?filters=preset:sd',
]
 */
export const getFeedQueryOptionsFromUrl = createCachedSelector(
  (state, location) =>
    location?.pathname ?? getRouterData(state, 'location.pathname'),
  (state, location) =>
    location?.search ?? getRouterData(state, 'location.search'),
  (pathname, search) => {
    const urlObject = new URL(`${pathname}${search}`, 'https://localhost');
    const feed = (
      urlObject.searchParams.get('feed') ||
      urlObject.searchParams.get('_feed') ||
      ''
    )
      .split(QUERY_SEPARATOR)
      .filter(name => name)
      .join('&');
    const filters = (
      urlObject.searchParams.get('filters') ||
      urlObject.searchParams.get('_filters') ||
      ''
    )
      .split(QUERY_SEPARATOR)
      .filter(query => query)
      .map(query => `?filters=${query}`);
    const sorts = (
      urlObject.searchParams.get('sorts') ||
      urlObject.searchParams.get('_sorts') ||
      ''
    )
      .split(QUERY_SEPARATOR)
      .filter(query => query)
      .map(query => `?sorting=${query}`);
    const queries = [feed, ...filters, ...sorts].filter(item => item);
    return queries;
  }
)((state, location) =>
  location
    ? `${location.pathname}${location.search}`
    : 'getFeedQueryOptionsFromUrl'
);
